/**
 * /russian-word-analysis — разбор слова: фонетический, по составу, морфологический.
 *
 * Одно поле: слово или предложение (слова предложения — кнопками, разбирается выбранное).
 * Три вкладки, каждая — карточка «как в тетради». Разбор СЧИТАЕТСЯ в браузере: правила
 * фонетики + словарь ударений, морфемный словарь (Викисловарь) + правила формообразования,
 * Az.js (OpenCorpora) для морфологии. Словари грузятся лениво — каждый, когда нужен его разбор.
 *
 * ⚠️ Движки — engine/*.js, чистые функции под node-тестами. Здесь только связывание со страницей.
 */

import { CFG, t, tf, code, esc, formatNum, ruText, join } from './i18n.js';
import { parseInput, sentenceText } from './tokens.js';
import * as D from './dicts.js';
import { renderPhon, onWord } from './phon-view.js';
import { renderMorph } from './morph-view.js';
import { renderSostav } from './sostav-view.js';
import { sheetText, sheetPng } from './export.js';
import { DEFAULT_SETTINGS as MS_DEFAULTS } from './engine/morphemes.js';
import { DEFAULT_SETTINGS as MO_DEFAULTS } from './engine/morphology.js';

const SLUG = 'russian-word-analysis';
// порядок вкладок — локали страницы (ru: состав, морфология, фонетика), первая — по умолчанию
const TABS = Array.isArray(CFG.tabs) && CFG.tabs.length === 3 ? CFG.tabs.slice() : ['phon', 'sostav', 'morph'];
// Полоса загрузки показывает словари вкладки; качать заранее фонетике нужно браузерный словарь
// ударений, только если базы ударений сайта (/api/ru-word) на этом домене нет.
const TAB_PARTS = { phon: ['accent'], sostav: ['morphemes', 'az'], morph: ['az'] };
const TAB_NEEDS = { phon: CFG.wordUrl ? [] : ['accent'], sostav: ['morphemes', 'az'], morph: ['az'] };
const HIST_KEY = 'rwa_history';
const $ = (id) => document.getElementById(id);

const S = {
    text: '',
    tokens: [],
    parsed: { text: '', tokens: [], foreign: [] },
    sel: 0,
    tab: TABS[0],
    words: new Map(),
    settings: { phon: { jot: 'й', longMark: ':', dentals: 'norm' }, sostav: Object.assign({}, MS_DEFAULTS), morph: Object.assign({}, MO_DEFAULTS) },
    sheet: null,
    state: 'empty',
    primed: false,
    renderSeq: 0,
};

/* ── телеметрия ───────────────────────────────────────────────────────────────
   По ней чинят то, чего не видно (QA 14.10: до этого «получил результат» отмечался только при выдаче —
   копии, PNG, PDF, печати, — а отказы ввода и «не умею» не доходили вовсе):
     stage()          шаги: ready → input → first_result;
     funnel()         ключевые действия — __aeFunnel: вкладка, пример, слово предложения, история,
                      настройка, ударение, омограф, вариант разбора, выдача каждого вида, «поделиться»,
                      озвучка, повтор загрузки словаря;
     cardOutcome()    карточка устоялась (SETTLE_MS — «под» на пути к «подснежник» событием не
                      становится): разбор — tool_complete ОДИН раз на результат (вкладка, слово
                      или предложение, источник разбора, часть речи, длина слова — без самого слова);
                      «не умею» (нет в словаре ударений, нет гласных, автоматический разбор, слово
                      неизвестно морфологии) — tool_diagnostic с кодом и словом до 60 знаков;
     fieldRefused()   отказ у поля (нет русских букв, чужие буквы, латиница) — tool_diagnostic с вводом
                      до 60 знаков: главный прибор «что люди пытаются разобрать».
   Слово для разбора — не сведения о человеке; в результат (tool_complete) оно не уходит. */
const SETTLE_MS = 1200;
const staged = {};
function stage(name, meta) {
    if (staged[name]) return;
    staged[name] = 1;
    try { if (window.__stage) window.__stage(name, meta || {}); } catch (_) { /* телеметрия не мешает работе */ }
}
function funnel(name, p) {
    try { if (window.__aeFunnel) window.__aeFunnel(name, Object.assign({ tab: S.tab }, p || {})); } catch (_) { /* телеметрия не мешает работе */ }
}
function diag(issue, input, extra) {
    try {
        if (window.__aeDiagnostic) window.__aeDiagnostic(Object.assign({ issue, tab: S.tab }, input == null ? {} : { input: String(input).slice(0, 60) }, extra || {}));
    } catch (_) { /* телеметрия не мешает работе */ }
}
let outcomeTimer = 0;
let outcomeSig = '';
let pending = null; // { sig, at, send } — карточка на экране, событие ещё не ушло
function sendOutcome(p) {
    clearTimeout(outcomeTimer);
    pending = null;
    if (p.sig === outcomeSig) return;
    outcomeSig = p.sig;
    p.send();
}
/**
 * Карточка показана: результат (complete) и/или «не умею» (issue) — когда она простояла SETTLE_MS, один
 * раз на подпись. Перерисовка той же карточки (пришёл словарь) отсчёт не сбрасывает; карточка, которая
 * простояла своё, уходит и тогда, когда человек сразу переключил вкладку.
 */
function cardOutcome(sig, complete, issue, input, extra) {
    if (complete || issue) stage('input');
    if (pending && pending.sig === sig) return;
    if (pending && Date.now() - pending.at >= SETTLE_MS) sendOutcome(pending);
    clearTimeout(outcomeTimer);
    const p = {
        sig, at: Date.now(),
        send: () => {
            if (complete) { try { if (window.__aeToolComplete) window.__aeToolComplete(complete); } catch (_) { /* телеметрия не мешает работе */ } }
            if (issue) diag(issue, input, extra);
        },
    };
    pending = p;
    outcomeTimer = setTimeout(() => sendOutcome(p), SETTLE_MS);
}
/** То же для истории слов: разбор, простоявший на карточке, — то, что человек разбирал. */
function cardSettled(sig) {
    const text = S.text;
    clearTimeout(histTimer);
    histTimer = setTimeout(() => { if (S.text === text && S.tokens.length) histPush(text); }, SETTLE_MS);
}
let histTimer = 0;
let fieldTimer = 0;
let fieldSig = '';
function fieldRefused(issue, input) {
    clearTimeout(fieldTimer);
    if (!issue) { fieldSig = ''; return; }
    stage('input');
    fieldTimer = setTimeout(() => {
        const sig = issue + '|' + input;
        if (sig === fieldSig) return;
        fieldSig = sig;
        diag(issue, input);
    }, SETTLE_MS);
}
/** Наша ошибка в коде: ярлык места и текст — в телеметрию, человеку — фраза. */
function caught(e, label, say) {
    try { if (window.__handleError) window.__handleError(e, label, say ? { say } : undefined); else console.error('[rwa] ' + label, e); } catch (_) { /* телеметрия не мешает работе */ }
}

/* ── настройки ── */
function loadSetting(key, def) {
    try { return window.__toolSettings ? window.__toolSettings.load(SLUG, key, def) : def; } catch (_) { return def; }
}
function saveSetting(key, value) {
    try { if (window.__toolSettings) window.__toolSettings.save(SLUG, key, value); } catch (_) { /* настройки не мешают работе */ }
}

/* Поле настройки → ключ движка. bool — чекбокс. */
const SETTINGS = [
    ['rwaSetJot', 'phon', 'jot'], ['rwaSetAssim', 'phon', 'dentals'], ['rwaSetLong', 'phon', 'longMark'],
    ['rwaSetInf', 'sostav', 'infinitive'], ['rwaSetVfs', 'sostav', 'verbFormSuffix'], ['rwaSetForm', 'sostav', 'formativeInStem', 'bool'],
    ['rwaSetPost', 'sostav', 'postfixInStem', 'bool'], ['rwaSetZero', 'sostav', 'zeroSuffix', 'bool'],
    ['rwaSetPart', 'morph', 'participle'], ['rwaSetGer', 'morph', 'gerund'],
];

function initSettings() {
    for (const [id, group, key, kind] of SETTINGS) {
        const el = $(id);
        if (!el) continue;
        const def = S.settings[group][key];
        const stored = loadSetting('s_' + key, def);
        if (kind === 'bool') {
            S.settings[group][key] = stored === true || stored === 'true';
            el.checked = S.settings[group][key];
        } else {
            el.value = String(stored);
            if (el.value !== String(stored)) el.value = String(def);
            S.settings[group][key] = el.value;
        }
        el.addEventListener('change', () => {
            S.settings[group][key] = kind === 'bool' ? el.checked : el.value;
            saveSetting('s_' + key, S.settings[group][key]);
            if (group === 'morph') D.setMorphSettings(S.settings.morph);
            funnel('setting', { key, value: String(S.settings[group][key]) });
            render();
        });
    }
    D.setMorphSettings(S.settings.morph);
}

/* ── ввод ── (слова поля — tokens.js) */
function wordState(tok) {
    const k = tok.low;
    if (!S.words.has(k)) S.words.set(k, { stress: null, hg: 0, lex: 0, read: 0, role: null });
    return S.words.get(k);
}

function showFieldError(msg, info) {
    const err = $('rwaErr');
    const inf = $('rwaInfo');
    const input = $('rwaIn');
    err.textContent = msg || '';
    err.hidden = !msg;
    if (msg) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid');
    inf.textContent = info || '';
    inf.hidden = !info;
}

/** Ошибка или пометка у поля: что из введённого не разбирается и почему. */
function fieldNotes(text, parsed) {
    // слово с буквами не русского алфавита не разбирается — и сказано почему (первое такое слово)
    const fx = parsed.foreign[0];
    const word = fx ? ruText(fx.word) : '';
    const letters = fx ? join(fx.letters.map(ruText)) : '';
    const why = !fx ? '' : fx.latin ? tf('err_mixed_latin', { word, letters }) : tf('err_foreign_letters', { word, letters });
    const latinSkipped = /[A-Za-z0-9]/.test(text) && !(fx && fx.latin);
    if (!text.trim()) showFieldError('', '');
    else if (!parsed.tokens.length) showFieldError(why || t('err_no_russian'), '');
    else showFieldError('', [why, latinSkipped ? t('info_skipped') : ''].filter(Boolean).join(' '));
    const issue = !text.trim() ? '' : fx ? (fx.latin ? 'mixed_latin' : 'foreign_letters') : !parsed.tokens.length ? 'no_russian' : latinSkipped ? 'latin_skipped' : '';
    fieldRefused(issue, text.trim());
}

function onInput(immediate) {
    const text = $('rwaIn').value;
    S.text = text;
    const parsed = parseInput(text);
    S.parsed = parsed;
    S.tokens = parsed.tokens;
    S.sel = Math.min(S.sel, Math.max(0, S.tokens.length - 1));
    fieldNotes(text, parsed);
    if (S.tokens.length) prime();
    renderWords();
    render();
    // в историю — сразу по Enter / примеру; набранное без Enter — когда его разбор простоял на карточке
    // (cardOutcome): по таймеру ввода в историю попадали обрывки «по», «зам», если телефон подвисал на
    // словаре посреди набора (QA 14.10)
    if (immediate) scheduleCommit(0);
}

function renderWords() {
    const box = $('rwaWords');
    if (S.tokens.length < 2) { box.hidden = true; box.innerHTML = ''; return; }
    box.hidden = false;
    box.innerHTML = '<span class="rwa-words-l">' + esc(t('pick_word')) + '</span>' + S.tokens.map((tk, i) => '<button type="button" class="tc-chip' + (i === S.sel ? ' on' : '')
        + '" lang="ru" dir="ltr" data-rwa-tok="' + i + '" aria-pressed="' + (i === S.sel ? 'true' : 'false') + '">' + esc(tk.plain) + '</button>').join('');
}

/* ── словари и полоса загрузки ── */
function prime() {
    if (S.primed) return;
    S.primed = true;
    if (!CFG.wordUrl) D.need('accent');
    D.need('morphemes');
}

function needForTab() {
    if (!S.tokens.length) return;
    // словарь, который отказал, сам не перезапрашивается: иначе каждая перерисовка после отказа заново
    // начинала загрузку — вечное «0,0 из 10,7 МБ» без «Повторить» и 10 запросов в секунду (QA 14.10);
    // повтор — кнопкой на полосе загрузки
    for (const p of TAB_NEEDS[S.tab]) if (D.status(p).status !== 'error') D.need(p);
}

function renderLoad() {
    const box = $('rwaLoad');
    const parts = TAB_PARTS[S.tab];
    let any = false;
    for (const row of box.querySelectorAll('[data-rwa-load]')) {
        const part = row.getAttribute('data-rwa-load');
        const s = D.status(part);
        const show = S.tokens.length > 0 && parts.includes(part) && (s.status === 'loading' || s.status === 'error');
        row.hidden = !show;
        if (!show) continue;
        any = true;
        const bar = row.querySelector('.rwa-bar i');
        const txt = row.querySelector('.rwa-load-v');
        const retry = row.querySelector('[data-rwa-retry]');
        row.classList.toggle('bad', s.status === 'error');
        bar.style.width = Math.round((s.fraction || 0) * 100) + '%';
        row.querySelector('.rwa-bar').setAttribute('aria-valuenow', String(Math.round((s.fraction || 0) * 100)));
        if (s.status === 'error') txt.textContent = t('load_failed');
        else if (s.total) txt.textContent = tf('load_mb', { a: mb(s.loaded), b: mb(s.total) });
        else txt.textContent = t('load_start');
        retry.hidden = s.status !== 'error';
    }
    box.hidden = !any;
}
const mb = (b) => formatNum(b / 1048576, 1);

/* ── карточка ── */
function setTab(tab, opts) {
    if (!TABS.includes(tab)) tab = TABS[0];
    S.tab = tab;
    document.querySelectorAll('[data-rwa-tab]').forEach((b) => {
        const on = b.getAttribute('data-rwa-tab') === tab;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
    });
    $('rwaCard').setAttribute('aria-labelledby', 'rwaTab-' + tab);
    if (!(opts && opts.silent)) render();
}

function emptyCard() {
    S.sheet = null;
    S.state = 'empty';
    $('rwaCardTitle').textContent = t('card_empty_title');
    $('rwaCardBody').innerHTML = '<p class="rwa-empty">' + esc(t('card_empty')) + '</p><div class="rwa-samples-big">'
        + (CFG.samples || []).slice(0, 8).map((s) => '<button type="button" class="tc-chip" lang="ru" dir="ltr" data-rwa-sample="' + esc(s) + '">' + esc(s) + '</button>').join('') + '</div>';
    setActions(false);
}

function setActions(on) {
    document.querySelectorAll('#rwaCard [data-rwa-act]').forEach((b) => { b.disabled = !on; });
    $('rwaActions').hidden = !on;
}

function waitCard(title, note) {
    $('rwaCardTitle').textContent = title;
    $('rwaCardBody').innerHTML = '<p class="rwa-note">' + esc(note) + '</p>';
    S.sheet = null;
    S.state = 'wait';
    setActions(false);
}

let cacheKey = '';
let cacheRes = null;
function morphResult(tok) {
    const M = D.morph();
    if (!M) return null;
    const sentence = S.tokens.length > 1;
    // текст предложения — без слов с чужими буквами и без пометок ударения (sentenceText): иначе
    // морфология делит его на слова по-своему («кіт» → «к», «т»), и номер слова указывает не туда
    const clean = sentence ? sentenceText(S.parsed) : '';
    const key = [sentence ? clean : tok.plain, S.sel, S.settings.morph.participle, S.settings.morph.gerund].join('|');
    if (key === cacheKey && cacheRes) return cacheRes;
    let res;
    try {
        res = sentence ? M.analyzeSentence(clean, morphIndex(M, clean)).result : M.analyzeWord(tok.plain);
    } catch (e) {
        caught(e, 'rwa_morphology');
        res = { error: 'empty' };
    }
    cacheKey = key;
    cacheRes = res;
    return res;
}

/** Номер выбранного слова среди слов морфологии: то же слово с тем же порядковым повтором. */
function morphIndex(M, clean) {
    const want = S.tokens[S.sel] ? S.tokens[S.sel].low : '';
    let nth = 0;
    for (let k = 0; k < S.sel; k++) if (S.tokens[k].low === want) nth++;
    const list = M.tokenize ? M.tokenize(clean) : [];
    for (const mt of list) {
        if (mt.text.toLowerCase() === want && nth-- === 0) return mt.index;
    }
    return S.sel;
}

async function render() {
    renderLoad();
    if (!S.tokens.length) { emptyCard(); updateUrl(); return; }
    needForTab();
    const seq = ++S.renderSeq;
    const tok = S.tokens[Math.min(S.sel, S.tokens.length - 1)];
    const ws = wordState(tok);
    let out;
    if (S.tab === 'phon') {
        out = renderPhon({ raw: tok.raw, ws, settings: S.settings.phon });
    } else if (S.tab === 'morph') {
        const res = morphResult(tok);
        if (!res) { waitCard(tf('card_title_morph', { word: ruText(tok.plain) }), D.status('az').status === 'error' ? t('dict_failed') : t('m_wait')); updateUrl(); return; }
        out = renderMorph({ res, word: tok.plain.toLowerCase(), ws, sentence: S.tokens.length > 1 });
    } else {
        const dict = D.morphemeDict();
        if (!dict) { waitCard(tf('card_title_sostav', { word: ruText(tok.plain) }), D.status('morphemes').status === 'error' ? t('dict_failed') : t('ms_wait')); updateUrl(); return; }
        const res = morphResult(tok);
        const lx = res && res.lexemes && res.lexemes.length ? res.lexemes[Math.min(ws.lex || 0, res.lexemes.length - 1)] : null;
        const words = [tok.plain, lx && lx.lemma, lx && lx.verb];
        let fresh = false;
        try {
            const p = D.ensureMorphemes(words);
            // прежняя карточка остаётся, только если это разбор ТОГО ЖЕ слова (перерисовка после
            // настройки или словаря); новое слово — «загружается», а не чужой разбор, который можно
            // скопировать или сохранить под новым именем (QA 14.10)
            if (!S.sheet || S.state !== 'ok' || S.cardKey !== cardKey(tok)) waitCard(tf('card_title_sostav', { word: ruText(tok.plain) }), t('ms_wait_shard'));
            fresh = await p;
        } catch (e) {
            caught(e, 'rwa_shard');
        }
        if (seq !== S.renderSeq) return;
        void fresh;
        out = renderSostav({ word: tok.plain, lexeme: lx, settings: S.settings.sostav, ws });
    }
    if (seq !== S.renderSeq) return;
    paintCard(out, tok);
    reportCard(out, tok, ws);
    updateUrl();
}

const cardKey = (tok) => S.tab + '|' + tok.low + '|' + S.sel;

function paintCard(out, tok) {
    $('rwaCardTitle').textContent = out.title || '';
    $('rwaCardBody').innerHTML = out.html;
    S.sheet = out.state === 'ok' ? out.sheet : null;
    S.state = out.state;
    S.cardKey = tok ? cardKey(tok) : '';
    S.speak = out.speak || (S.tokens[S.sel] && S.tokens[S.sel].plain);
    setActions(out.state === 'ok');
    if (out.state === 'ok') stage('first_result');
}

/** Что показала карточка — в телеметрию (cardOutcome): результат и/или «не умею». */
function reportCard(out, tok, ws) {
    const sig = [cardKey(tok), ws.stress, ws.hg, ws.lex, ws.read, out.state].join('|');
    if (out.state === 'ok' || out.state === 'need') cardSettled(sig);
    const base = { tab: S.tab, mode: S.tokens.length > 1 ? 'sentence' : 'word', len: tok.plain.replace(/-/g, '').length, hyph: tok.plain.includes('-') ? 1 : 0 };
    const word = tok.plain.toLowerCase();
    if (S.tab === 'phon') {
        const m = out.meta || {};
        if (out.state === 'ok') cardOutcome(sig, Object.assign(base, { src: m.src, letters: m.letters, sounds: m.sounds }), m.src === 'guess' ? 'phon_stress_guessed' : '', word);
        else if (out.state === 'need' && m.src !== 'dict_failed') cardOutcome(sig, null, m.src === 'engine_null' ? 'phon_engine_refused' : 'phon_no_stress', word);
        else if (out.state === 'error') cardOutcome(sig, null, 'phon_engine_missing', null);
    } else if (S.tab === 'sostav') {
        const r = out.res || {};
        const issue = r.source === 'auto' ? 'sostav_not_in_dict' : r.source === 'auto_stem' ? 'sostav_unsure' : '';
        if (out.state === 'ok') cardOutcome(sig, Object.assign(base, { src: r.source, pos: r.pos || undefined, why: r.auto && r.auto.why ? r.auto.why : undefined }), issue, word);
        else if (out.state === 'error') cardOutcome(sig, null, 'sostav_error', word);
    } else if (out.state === 'ok') {
        const lx = out.lexeme || {};
        const res = cacheRes || {};
        const predicted = !!(lx.predicted || res.predicted);
        cardOutcome(sig, Object.assign(base, { pos: lx.pos, lexemes: res.lexemes ? res.lexemes.length : 0, src: predicted ? 'predicted' : 'dict' }), predicted ? 'morph_predicted' : '', word);
    } else if (out.state === 'error') cardOutcome(sig, null, 'morph_unknown', word);
}

/* ── история последних слов (localStorage: удобство, не работа) ── */
function histLoad() {
    try { const v = JSON.parse(localStorage.getItem(HIST_KEY) || '[]'); return Array.isArray(v) ? v.filter((x) => typeof x === 'string').slice(0, 12) : []; } catch (_) { return []; }
}
function histSave(list) {
    try { localStorage.setItem(HIST_KEY, JSON.stringify(list.slice(0, 12))); } catch (_) { /* приватное окно */ }
}
const histRecent = new Map(); // запись → когда добавлена на этой странице
function histPush(text) {
    const v = String(text || '').trim().replace(/\s+/g, ' ');
    if (!v || v.length > 120) return;
    const low = v.toLowerCase();
    const now = Date.now();
    // обрывок набора («по» перед «подснежник»), записанный этой страницей только что, — не слово человека
    const stub = (x) => x.length < v.length && low.startsWith(x.toLowerCase()) && histRecent.has(x) && now - histRecent.get(x) < 30000;
    const list = histLoad().filter((x) => x.toLowerCase() !== low && !stub(x));
    histRecent.set(v, now);
    list.unshift(v);
    histSave(list);
    renderHist();
}
function renderHist() {
    const list = histLoad();
    const box = $('rwaHist');
    $('rwaHistBox').hidden = !list.length;
    box.innerHTML = list.map((w) => '<button type="button" class="tc-chip" lang="ru" dir="ltr" data-rwa-sample="' + esc(w) + '">' + esc(w.length > 28 ? w.slice(0, 26) + '…' : w) + '</button>').join('');
}

let commitTimer = 0;
function scheduleCommit(ms) {
    clearTimeout(commitTimer);
    commitTimer = setTimeout(() => {
        if (!S.tokens.length) return;
        histPush(S.text);
    }, ms);
}

/* ── адресная строка: ?w=слово&t=phon|sostav|morph (+ i — слово предложения, s — ударение) ── */
let urlTimer = 0;
function updateUrl() {
    clearTimeout(urlTimer);
    urlTimer = setTimeout(() => {
        try {
            const u = shareUrl(false);
            history.replaceState(history.state, '', u.pathname + (u.search ? u.search : '') + u.hash);
        } catch (_) { /* песочница */ }
    }, 200);
}

function readUrl() {
    try {
        const q = new URLSearchParams(location.search);
        const tab = q.get('t');
        if (tab && TABS.includes(tab)) S.tab = tab;
        const w = q.get('w');
        if (!w) return false;
        $('rwaIn').value = w.slice(0, 200);
        S.text = $('rwaIn').value;
        const parsed = parseInput(S.text);
        S.parsed = parsed;
        S.tokens = parsed.tokens;
        fieldNotes(S.text, parsed);
        const i = Number(q.get('i'));
        if (Number.isInteger(i) && i >= 0 && i < S.tokens.length) S.sel = i;
        const s = q.get('s');
        if (s != null && s !== '' && S.tokens.length) {
            const n = Number(s);
            if (Number.isInteger(n)) wordState(S.tokens[S.sel]).stress = n;
        }
        return S.tokens.length > 0;
    } catch (_) { return false; }
}

/* ── выдача ── */
function footText() {
    return CFG.site ? tf('card_foot_site', { site: CFG.site, title: t('card_foot') }) : t('card_foot');
}

async function copy() {
    const text = sheetText(S.sheet, footText() + (S.sheet && S.sheet.credit ? '\n' + CFG.creditText : ''));
    if (!text) return;
    // запись в буфер — в том же нажатии, без ожидания до неё (Safari иначе отказывает)
    const ok = window.__copyText ? await window.__copyText(text) : await navigator.clipboard.writeText(text).then(() => true, () => false);
    if (window.__toast) window.__toast(ok ? t('copied') : t('copy_failed'), ok ? 'success' : 'error');
    funnel('export', { via: 'copy', ok: ok ? 1 : 0 });
    if (!ok) diag('export_failed', null, { via: 'copy' });
}

/** Только транскрипция — в буфер, в том же нажатии (Safari). */
function copyTranscription(tr) {
    if (!tr) return;
    const p = window.__copyText ? window.__copyText(tr) : navigator.clipboard.writeText(tr).then(() => true, () => false);
    Promise.resolve(p).then((ok) => {
        if (window.__toast) window.__toast(ok ? t('copied_tr') : t('copy_failed'), ok ? 'success' : 'error');
        funnel('export', { via: 'transcription', ok: ok ? 1 : 0 });
    });
}

function fileBase() {
    const w = S.tokens[S.sel] ? S.tokens[S.sel].plain : 'word';
    const base = code('file', S.tab) + '-' + w;
    try { if (window.__outputName) return window.__outputName.sanitize(base); } catch (_) { /* имя ниже */ }
    return base.replace(/[^\p{L}\p{N}_-]+/gu, '_');
}

async function png(btn) {
    if (!S.sheet) return;
    btn.disabled = true;
    try {
        const blob = await sheetPng(S.sheet, S.sheet.credit ? tf('card_foot_credit', { foot: footText(), credit: CFG.creditText }) : footText());
        const name = fileBase() + '.png';
        const ok = window.__downloadFile ? await window.__downloadFile(blob, name) : false;
        funnel('export', { via: 'png', ok: ok === false ? 0 : 1, kb: Math.round(blob.size / 1024) });
    } catch (e) {
        caught(e, 'rwa_png', t('png_error'));
        diag('export_failed', null, { via: 'png' });
    } finally {
        btn.disabled = false;
    }
}

/** Лист PDF — клон карточки без кнопок и переключателей. */
function buildReport() {
    const root = document.createElement('div');
    root.className = 'lg-app rwa rwa-report';
    root.style.cssText = 'padding:0;max-width:none';
    const card = $('rwaCard').cloneNode(true);
    card.removeAttribute('id');
    card.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    // заголовок листа ставит сам генератор PDF (title) — второй, из карточки, не нужен
    card.querySelectorAll('.no-print, #rwaActions, [data-rwa-act], .rwa-card-t').forEach((el) => el.remove());
    card.style.cssText = 'border:0;box-shadow:none;backdrop-filter:none;padding:0;background:transparent';
    root.appendChild(card);
    if (S.sheet && S.sheet.credit) {
        const c = document.createElement('p');
        c.className = 'rwa-credit';
        c.textContent = CFG.creditText;
        root.appendChild(c);
    }
    return root;
}

function pdf(btn) {
    if (!S.sheet) return;
    // сборщик PDF (report-pdf.js) мог не доехать — кнопка тогда не молчит
    if (!window.__generatePDF) {
        if (window.__toast) window.__toast(t('pdf_error'), 'error');
        diag('pdf_engine_missing');
        return;
    }
    btn.disabled = true;
    window.__generatePDF({
        element: buildReport(),
        title: S.sheet.title,
        filename: fileBase() + '.pdf',
        strings: { libError: t('pdf_error'), genError: t('pdf_error'), blocked: t('pdf_blocked') },
        pagebreak: { mode: ['css', 'legacy'] },
    }).then((r) => {
        btn.disabled = false;
        funnel('export', { via: 'pdf', ok: r && r.ok ? 1 : 0, code: r && r.code });
        if (!(r && r.ok)) diag('export_failed', null, { via: 'pdf', code: r && r.code });
    }, (e) => { btn.disabled = false; caught(e, 'rwa_pdf', t('pdf_error')); });
}

/** Адрес этого разбора (тот же, что пишет updateUrl) — сразу, без ожидания таймера. */
function shareUrl(forShare) {
    const u = new URL(location.href);
    for (const k of forShare ? ['w', 't', 'i', 's', 'nocache'] : ['w', 't', 'i', 's']) u.searchParams.delete(k);
    if (S.tokens.length) {
        u.searchParams.set('w', S.text.trim());
        u.searchParams.set('t', S.tab);
        if (S.tokens.length > 1) u.searchParams.set('i', String(S.sel));
        const ws = S.words.get(S.tokens[S.sel].low);
        if (ws && ws.stress != null) u.searchParams.set('s', String(ws.stress));
    } else if (S.tab !== TABS[0]) {
        u.searchParams.set('t', S.tab);
    }
    return u;
}

/*
 * «Поделиться»: адрес собирается сразу, и системное окно / запись в буфер зовутся В ТОМ ЖЕ нажатии.
 * До QA 14.10 здесь стояло ожидание 260 мс (пока таймер допишет адрес) — Safari после него считает
 * нажатие закончившимся и отказывает и navigator.share, и записи в буфер.
 */
function share() {
    let url = location.href;
    try {
        const u = shareUrl(false);
        clearTimeout(urlTimer);
        history.replaceState(history.state, '', u.pathname + u.search + u.hash);
        url = shareUrl(true).href;
    } catch (_) { /* песочница — делимся текущим адресом */ }
    if (navigator.share && matchMedia('(pointer:coarse)').matches) {
        navigator.share({ title: document.title, url }).then(
            () => funnel('share', { via: 'native' }),
            (e) => { if (!(e && e.name === 'AbortError')) shareCopy(url); },
        );
        return;
    }
    shareCopy(url);
}
function shareCopy(url) {
    const p = window.__copyText ? window.__copyText(url) : navigator.clipboard.writeText(url).then(() => true, () => false);
    Promise.resolve(p).then((ok) => {
        if (window.__toast) window.__toast(ok ? t('link_copied') : t('copy_failed'), ok ? 'success' : 'error');
        funnel('share', { via: 'clipboard', ok: ok ? 1 : 0 });
    });
}

function say() {
    const T = window.TTSHelper;
    const word = S.speak;
    if (!T || !T.isSupported() || !word) { if (window.__toast) window.__toast(t('say_unsupported'), 'warning'); diag('say_unsupported'); return; }
    const voices = (T.getVoices && T.getVoices('ru')) || [];
    if (!voices.length) { if (window.__toast) window.__toast(t('say_no_voice'), 'warning'); diag('say_no_voice'); return; }
    T.speak(word, { lang: 'ru-RU', rate: 0.85, onerror: () => { if (window.__toast) window.__toast(t('say_no_voice'), 'warning'); } });
    funnel('say');
}

/* ── события ── */
function onClick(e) {
    const el = e.target.closest('button, [data-rwa-sample]');
    if (!el || !$('rwaApp').contains(el)) return;
    const d = el.dataset;
    const tok = S.tokens[S.sel];
    const ws = tok ? wordState(tok) : null;
    if (d.rwaSample != null || d.rwaWord != null) {
        const v = d.rwaSample != null ? d.rwaSample : d.rwaWord;
        $('rwaIn').value = v;
        S.sel = 0;
        funnel(d.rwaSample != null ? (el.closest('#rwaHist') ? 'history' : 'sample') : 'family_word');
        onInput(true);
        return;
    }
    if (d.rwaTok != null) { S.sel = Number(d.rwaTok); renderWords(); render(); funnel('pick_word', { n: S.tokens.length }); return; }
    if (d.rwaTab) { setTab(d.rwaTab); funnel('tab'); return; }
    if (d.rwaStress != null && ws) { ws.stress = Number(d.rwaStress); funnel('stress_pick'); render(); return; }
    if (d.rwaHg != null && ws) { ws.hg = Number(d.rwaHg); ws.stress = null; funnel('homograph_pick'); render(); return; }
    if (d.rwaLex != null && ws) { ws.lex = Number(d.rwaLex); ws.read = 0; ws.role = null; cacheKey = ''; funnel('lexeme_pick'); render(); return; }
    if (d.rwaRead != null && ws) { ws.read = Number(d.rwaRead); ws.role = null; funnel('reading_pick'); render(); return; }
    if (d.rwaRole != null && ws) { ws.role = ws.role === d.rwaRole ? null : d.rwaRole; funnel('role_pick'); render(); return; }
    if (d.rwaRetry != null) { D.need(d.rwaRetry); renderLoad(); funnel('retry', { part: d.rwaRetry }); return; }
    if (d.rwaHistClear != null) { histSave([]); renderHist(); return; }
    const act = d.rwaAct;
    if (!act) return;
    if (act === 'stress-reset' && ws) { ws.stress = null; render(); }
    else if (act === 'say') say();
    else if (act === 'copy') copy();
    else if (act === 'copy-tr') copyTranscription(el.getAttribute('data-tr') || '');
    else if (act === 'png') png(el);
    else if (act === 'pdf') pdf(el);
    else if (act === 'print') { funnel('export', { via: 'print' }); window.print(); }
    else if (act === 'share') share();
}

function onTabKey(e) {
    const b = e.target.closest('[data-rwa-tab]');
    if (!b) return;
    const i = TABS.indexOf(b.getAttribute('data-rwa-tab'));
    let n = -1;
    const rtl = document.dir === 'rtl' || document.documentElement.dir === 'rtl';
    if (e.key === 'ArrowRight') n = rtl ? i - 1 : i + 1;
    else if (e.key === 'ArrowLeft') n = rtl ? i + 1 : i - 1;
    else if (e.key === 'Home') n = 0;
    else if (e.key === 'End') n = TABS.length - 1;
    if (n < 0 && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    n = (n + TABS.length) % TABS.length;
    setTab(TABS[n]);
    const nb = document.querySelector('[data-rwa-tab="' + TABS[n] + '"]');
    if (nb) nb.focus();
}

function init() {
    if (!$('rwaApp')) return;
    initSettings();
    const fromUrl = readUrl();
    setTab(S.tab, { silent: true });
    renderHist();
    // база ударений сайта ответила — фонетика этого слова перерисовывается
    onWord((w) => { if (S.tab === 'phon' && S.tokens[S.sel] && S.tokens[S.sel].low.replace(/-/g, '') === w) render(); });
    D.onChange((part, kind) => {
        renderLoad();
        // словарь пришёл (или отказал) — разбор, который мог его ждать, перерисовывается
        if (kind === 'status' && S.tokens.length) { cacheKey = ''; render(); }
    });
    const input = $('rwaIn');
    let timer = 0;
    input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(() => onInput(false), 180); });
    input.addEventListener('focus', prime, { once: true });
    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); clearTimeout(timer); onInput(true); }
    });
    $('rwaApp').addEventListener('click', onClick);
    document.querySelector('.rwa-tabs').addEventListener('keydown', onTabKey);
    if (fromUrl) { prime(); renderWords(); }
    render();
    stage('ready');

    // Опора для стенда: что сейчас на карточке — снаружи.
    window.__rwaState = () => ({
        tab: S.tab, text: S.text, sel: S.sel, state: S.state, title: S.sheet ? S.sheet.title : '',
        sheet: sheetText(S.sheet), dicts: Object.fromEntries(D.PARTS.map((p) => [p, D.status(p).status])),
    });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

